import React, { useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  QrCode, 
  Copy, 
  Download, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  Zap
} from 'lucide-react';

interface GeneratedQR {
  id: string;
  content: string;
  qrData: string;
  timestamp: Date;
}

const QRCodeGenerator: React.FC = () => {
  const [content, setContent] = useState<string>('https://example.com');
  const [qrCode, setQrCode] = useState<GeneratedQR | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  // SEO Meta Data
  const seoData = useMemo(() => {
    const title = qrCode 
      ? `QR Code Generator - Generated QR Code for "${content.substring(0, 50)}${content.length > 50 ? '...' : ''}"`
      : "QR Code Generator - Create Custom QR Codes Online";
    
    const description = qrCode
      ? `QR code generated for "${content}". Free online QR code generator for URLs, text, and more. Download as PNG or copy to clipboard.`
      : "Generate custom QR codes instantly for URLs, text, and any content. Free QR code maker with download and copy functionality. Create QR codes in seconds.";

    const keywords = [
      "qr code generator",
      "free qr code maker", 
      "custom qr code",
      "qr code creator",
      "online qr generator",
      "url qr code",
      "text qr code",
      "qr code download",
      "instant qr code",
      "qr code tool"
    ].join(", ");

    return { title, description, keywords };
  }, [content, qrCode]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "QR Code Generator",
    "description": seoData.description,
    "url": "https://example.com/qr-code-generator",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Instant QR code generation",
      "URL and text QR codes",
      "Download as PNG",
      "Copy to clipboard",
      "Mobile-friendly interface",
      "Dark/Light themes"
    ],
    "browserRequirements": "Requires JavaScript enabled",
    "publisher": {
      "@type": "Organization",
      "name": "QR Tools"
    },
    "softwareVersion": "1.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free to use for personal and commercial purposes"
  };

  // Generate QR code data (placeholder - in production, use a real QR library)
  const generateQRCodeData = useCallback((inputContent: string): string => {
    // This is a placeholder - in production, use libraries like 'qrcode' or 'react-qr-code'
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const size = 300;
    canvas.width = size;
    canvas.height = size;
    
    if (ctx) {
      // Background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      
      // Generate a pattern based on content hash
      const hash = inputContent.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      
      const moduleSize = Math.floor(size / 25);
      ctx.fillStyle = '#000000';
      
      // Create a deterministic pattern based on content
      for (let i = 0; i < 25; i++) {
        for (let j = 0; j < 25; j++) {
          const seedValue = (hash + i * 25 + j) % 1000;
          if (seedValue % 3 === 0) {
            ctx.fillRect(i * moduleSize, j * moduleSize, moduleSize, moduleSize);
          }
        }
      }
      
      // Add corner squares (finder patterns)
      const cornerSize = moduleSize * 7;
      [[0, 0], [18 * moduleSize, 0], [0, 18 * moduleSize]].forEach(([x, y]) => {
        ctx.fillStyle = '#000000';
        ctx.fillRect(x, y, cornerSize, cornerSize);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(x + moduleSize, y + moduleSize, cornerSize - 2 * moduleSize, cornerSize - 2 * moduleSize);
        ctx.fillStyle = '#000000';
        ctx.fillRect(x + 2 * moduleSize, y + 2 * moduleSize, cornerSize - 4 * moduleSize, cornerSize - 4 * moduleSize);
      });
      
      // Add center square for longer content
      if (inputContent.length > 10) {
        const centerX = 9 * moduleSize;
        const centerY = 9 * moduleSize;
        const centerSize = 7 * moduleSize;
        ctx.fillStyle = '#000000';
        ctx.fillRect(centerX, centerY, centerSize, centerSize);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(centerX + moduleSize, centerY + moduleSize, centerSize - 2 * moduleSize, centerSize - 2 * moduleSize);
        ctx.fillStyle = '#000000';
        ctx.fillRect(centerX + 2 * moduleSize, centerY + 2 * moduleSize, centerSize - 4 * moduleSize, centerSize - 4 * moduleSize);
      }
    }
    
    return canvas.toDataURL('image/png');
  }, []);

  const generateQR = useCallback(async () => {
    if (!content.trim()) return;
    
    setIsGenerating(true);
    
    // Simulate processing time
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const qrData = generateQRCodeData(content);
    
    const newQR: GeneratedQR = {
      id: Math.random().toString(36).substr(2, 9),
      content: content.trim(),
      qrData,
      timestamp: new Date()
    };
    
    setQrCode(newQR);
    setIsGenerating(false);
  }, [content, generateQRCodeData]);

  const copyToClipboard = async () => {
    if (!qrCode) return;
    
    try {
      // Try to copy the image data
      const response = await fetch(qrCode.qrData);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback to copying the content
      try {
        await navigator.clipboard.writeText(qrCode.content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy to clipboard');
      }
    }
  };

  const downloadQR = () => {
    if (!qrCode) return;
    
    const link = document.createElement('a');
    link.download = `qr-code-${Date.now()}.png`;
    link.href = qrCode.qrData;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isGenerating) {
      generateQR();
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode 
        ? 'dark bg-gray-900' 
        : 'bg-gradient-to-br from-indigo-50 via-white to-purple-50'
    }`}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="QR Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/qr-code-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/qr-code-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/qr-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Simple QR Code Generator Tool" />
        <meta property="og:site_name" content="QR Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/qr-code-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/qr-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Free QR Code Generator - Instant Results" />
        <meta name="twitter:creator" content="@qrtools" />
        <meta name="twitter:site" content="@qrtools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#6366f1"} />
        <meta name="msapplication-TileColor" content="#6366f1" />
        <meta name="application-name" content="QR Code Generator" />
        <meta name="apple-mobile-web-app-title" content="QR Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How do I generate a QR code?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Simply enter your text or URL in the input box and click Generate. Your QR code will be created instantly and can be downloaded or copied to clipboard."
                }
              },
              {
                "@type": "Question", 
                "name": "What can I put in a QR code?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can create QR codes for URLs, plain text, email addresses, phone numbers, WiFi passwords, or any text-based content. The QR code will encode your input for easy scanning."
                }
              },
              {
                "@type": "Question",
                "name": "Can I download the QR code?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! After generating your QR code, you can download it as a PNG image file or copy it to your clipboard for immediate use."
                }
              },
              {
                "@type": "Question",
                "name": "Is this QR code generator free?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, this QR code generator is completely free to use. Generate as many QR codes as you need for personal or commercial use without any limitations."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Schema for Generated QR */}
        {qrCode && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "DigitalDocument",
              "name": `QR Code for ${qrCode.content}`,
              "description": `Generated QR code containing: ${qrCode.content}`,
              "encodingFormat": "image/png",
              "dateCreated": qrCode.timestamp.toISOString(),
              "creator": {
                "@type": "SoftwareApplication",
                "name": "QR Code Generator"
              }
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <QrCode className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  QR Code Generator
                </h1>
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Enter any text or URL to generate a QR code instantly
              </p>
            </div>

            {/* Input Section */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
              <div className="space-y-4">
                <div>
                  <label htmlFor="qr-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Enter text or URL:
                  </label>
                  <input
                    id="qr-input"
                    type="text"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="https://example.com or any text..."
                    className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                    disabled={isGenerating}
                  />
                </div>
                
                <button
                  onClick={generateQR}
                  disabled={isGenerating || !content.trim()}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 text-lg"
                >
                  {isGenerating ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Zap className="w-5 h-5" />
                  )}
                  <span>{isGenerating ? 'Generating...' : 'Generate QR Code'}</span>
                </button>
              </div>
            </div>

            {/* QR Code Display */}
            {qrCode && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Your QR Code
                </h3>
                
                <div className="inline-block p-6 bg-white rounded-lg shadow-inner mb-6">
                  <img
                    src={qrCode.qrData}
                    alt={`QR Code for ${qrCode.content}`}
                    className="w-64 h-64 mx-auto"
                  />
                </div>
                
                <div className="mb-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Content:</p>
                  <p className="text-gray-900 dark:text-white font-mono text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded break-all">
                    {qrCode.content}
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors font-medium"
                  >
                    {copied ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                    <span>{copied ? 'Copied!' : 'Copy to Clipboard'}</span>
                  </button>
                  
                  <button
                    onClick={downloadQR}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download PNG</span>
                  </button>
                </div>
              </div>
            )}

            {/* Help Text */}
            {!qrCode && (
              <div className="text-center">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-2">
                    How to use
                  </h3>
                  <p className="text-blue-700 dark:text-blue-300">
                    Enter any text, URL, email, or phone number in the input box above and click "Generate QR Code". 
                    Your QR code will be created instantly and can be downloaded or copied for use anywhere.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;